/**
 * Top-level app router. Public marketing/auth surfaces are unguarded;
 * `/my-documents` and friends are wrapped in `<RequireUser>`; `/admin/*`
 * is wrapped in `<RequireAdmin>`.
 */

import { Navigate, Route, Routes } from 'react-router-dom';
import { SessionProvider } from './hooks/useSession';
import { RequireAdmin, RequireAnon, RequireUser } from './components/layout/RouteGuards';

import { HomePage } from './routes/HomePage';
import { WaitlistPage } from './routes/WaitlistPage';
import { WaitlistConfirmedPage } from './routes/WaitlistConfirmedPage';
import { WaitlistStatusPage } from './routes/WaitlistStatusPage';
import { InvitePage } from './routes/InvitePage';
import { SignInPage } from './routes/SignInPage';
import { SignInSentPage } from './routes/SignInSentPage';
import { SignUpPage } from './routes/SignUpPage';
import { SuspendedPage } from './routes/SuspendedPage';
import { SharedAnalysisPage } from './routes/SharedAnalysisPage';
import { ReportPreviewPage } from './routes/ReportPreviewPage';
import { DemoPage } from './routes/DemoPage';

import { MyDocumentsPage } from './routes/MyDocumentsPage';
import { DocumentPage } from './routes/DocumentPage';
import { CreditsPage } from './routes/CreditsPage';
import { SettingsPage } from './routes/SettingsPage';

import { AdminLayout } from './routes/admin/AdminLayout';
import { AdminUsersPage } from './routes/admin/AdminUsersPage';
import { AdminUserDetailPage } from './routes/admin/AdminUserDetailPage';
import { AdminWaitlistPage } from './routes/admin/AdminWaitlistPage';
import { AdminDemoLinksPage } from './routes/admin/AdminDemoLinksPage';
import { AdminMetricsPage } from './routes/admin/AdminMetricsPage';

export function App() {
  return (
    <SessionProvider>
      <Routes>
        {/* Public marketing / auth */}
        <Route path="/" element={<HomePage />} />
        <Route path="/waitlist" element={<WaitlistPage />} />
        <Route path="/waitlist/confirmed" element={<WaitlistConfirmedPage />} />
        <Route path="/waitlist/status" element={<WaitlistStatusPage />} />
        <Route path="/invite" element={<InvitePage />} />
        <Route
          path="/sign-in"
          element={
            <RequireAnon>
              <SignInPage />
            </RequireAnon>
          }
        />
        <Route
          path="/sign-in/sent"
          element={
            <RequireAnon>
              <SignInSentPage />
            </RequireAnon>
          }
        />
        <Route
          path="/sign-up"
          element={
            <RequireAnon>
              <SignUpPage />
            </RequireAnon>
          }
        />
        <Route path="/suspended" element={<SuspendedPage />} />
        <Route path="/shared/:id" element={<SharedAnalysisPage />} />
        <Route path="/report-preview" element={<ReportPreviewPage />} />
        <Route path="/demo" element={<DemoPage />} />

        {/* App — gated */}
        <Route
          path="/my-documents"
          element={
            <RequireUser>
              <MyDocumentsPage />
            </RequireUser>
          }
        />
        <Route
          path="/documents/:id"
          element={
            <RequireUser>
              <DocumentPage />
            </RequireUser>
          }
        />
        <Route
          path="/credits"
          element={
            <RequireUser>
              <CreditsPage />
            </RequireUser>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireUser>
              <SettingsPage />
            </RequireUser>
          }
        />

        {/* Admin — gated AND nested */}
        <Route
          path="/admin"
          element={
            <RequireAdmin>
              <AdminLayout />
            </RequireAdmin>
          }
        >
          <Route index element={<Navigate to="users" replace />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="users/:id" element={<AdminUserDetailPage />} />
          <Route path="waitlist" element={<AdminWaitlistPage />} />
          <Route path="demo-links" element={<AdminDemoLinksPage />} />
          <Route path="metrics" element={<AdminMetricsPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </SessionProvider>
  );
}
